import React from "react";
import type { ReactElement } from "react";

import type { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import { createStackNavigator } from "@react-navigation/stack";
import type { StackNavigationProp } from "@react-navigation/stack";

import type { HubTabNavigatorProps } from "pianofunclub-crm/navigation/HubNavigator";
import defaultNavOptions from "pianofunclub-crm/navigation/utils/defaultNavigationOptions";
import type {
  AccountParams,
  EnrolmentParams,
  SchoolParams,
  InvoiceParams,
  DefaultTermDatesParams,
} from "pianofunclub-crm/navigation/utils/screenParams";
import AccountScreen, {
  screenOptions as accountScreenOptions,
} from "pianofunclub-crm/screens/accounts/AccountScreen";
import InvoiceScreen, {
  screenOptions as invoiceScreenOptions,
} from "pianofunclub-crm/screens/invoicing/InvoiceScreen";
import RegistersHubScreen, {
  screenOptions as registersHubScreenOptions,
} from "pianofunclub-crm/screens/registers/RegistersHubScreen";
import DefaultTermDatesScreen, {
  screenOptions as defaultTermDatesScreenOptions,
} from "pianofunclub-crm/screens/schools/DefaultTermDatesScreen";
import SchoolScreen, {
  screenOptions as schoolScreenOptions,
} from "pianofunclub-crm/screens/schools/SchoolScreen";
import EnrolmentScreen, {
  screenOptions as enrolmentScreenOptions,
} from "pianofunclub-crm/screens/waitingList/EnrolmentScreen";

export type RegistersStackNavigatorParamList = {
  Account: AccountParams;
  DefaultTermDates: DefaultTermDatesParams;
  Enrolment: EnrolmentParams;
  Invoice: InvoiceParams;
  RegistersHub?: {
    block?: number;
    searchTerm?: string;
    startingYear?: number;
  };
  School: SchoolParams;
};

type NavigatorProps<T extends keyof RegistersStackNavigatorParamList> = StackNavigationProp<
  RegistersStackNavigatorParamList,
  T
>;

export type RegistersStackNavigatorProps<T extends keyof RegistersStackNavigatorParamList> =
  CompositeNavigationProp<NavigatorProps<T>, HubTabNavigatorProps<"RegistersHubTab">>;

export type RegistersStackRouteProps<T extends keyof RegistersStackNavigatorParamList> = RouteProp<
  RegistersStackNavigatorParamList,
  T
>;

const RegistersStackNavigator = createStackNavigator<RegistersStackNavigatorParamList>();

const RegistersNavigator = (): ReactElement => {
  return (
    <RegistersStackNavigator.Navigator screenOptions={defaultNavOptions}>
      <RegistersStackNavigator.Screen
        component={RegistersHubScreen}
        name="RegistersHub"
        options={registersHubScreenOptions}
      />
      <RegistersStackNavigator.Screen
        component={AccountScreen}
        name="Account"
        options={accountScreenOptions}
      />
      <RegistersStackNavigator.Screen
        component={SchoolScreen}
        name="School"
        options={schoolScreenOptions}
      />
      <RegistersStackNavigator.Screen
        component={InvoiceScreen}
        name="Invoice"
        options={invoiceScreenOptions}
      />
      <RegistersStackNavigator.Screen
        component={DefaultTermDatesScreen}
        name="DefaultTermDates"
        options={defaultTermDatesScreenOptions}
      />
      <RegistersStackNavigator.Screen
        component={EnrolmentScreen}
        name="Enrolment"
        options={enrolmentScreenOptions}
      />
    </RegistersStackNavigator.Navigator>
  );
};

export default RegistersNavigator;
